import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { SectionCard, Pill, ActionButton, EmptyState, LoadingState } from "@/components/shared";
import { RecordAcceptanceDialog, RecordPaymentDialog } from "@/pages/sales/ReconcileDialogs";
import { usd, PAYMENT_METHOD_LABELS } from "@/pages/sales/common";

const STATUS_META = {
  needs_reconciliation: { label: "Needs Reconciliation", tone: "warning", icon: "AlertCircle" },
  already_reconciled: { label: "Already Reconciled", tone: "success", icon: "CheckCircle2" },
  advisory: { label: "Advisory", tone: "neutral", icon: "Info" },
};
const EV_ICON = { accepted: "CheckCircle", payment: "Banknote", fee_quoted: "Tag", estimate_sent: "Send", declined: "XCircle" };

// Allison — deterministic Historical-Conversation reconciliation. ADVISORY ONLY. Staff confirm
// via the SAME governed Increment-1 dialogs; Allison never mutates the estimate/payment lifecycle.
export default function AllisonReconciliationPanel({ clientId, clientName, onReconciled }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {kind, estimateId, prefill, currency}

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/sales/estimates/reconciliation-proposals/${clientId}`); setData(data); }
    catch (e) { setData(null); } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const done = () => { load(); onReconciled?.(); };

  const act = (p) => {
    const code = p.suggested_action.code;
    const eid = p.canonical_state?.estimate_id;
    if (code === "record_acceptance" && eid) setDialog({ kind: "acceptance", estimateId: eid, prefill: p.prefill });
    else if (code === "record_external_payment" && eid) setDialog({ kind: "payment", estimateId: eid, prefill: p.prefill, currency: p.prefill?.currency || p.detected?.currency || "USD" });
    else if (code === "create_estimate") navigate(`/sales/estimates?create=1&client=${clientId}`);
    else if (eid) navigate(`/sales/estimates/${eid}`);
  };

  if (loading) return <SectionCard title="Allison — Historical Reconciliation" icon="Sparkles"><LoadingState label="Scanning historical conversations…" /></SectionCard>;
  const proposals = data?.proposals || [];
  if (proposals.length === 0) {
    return (
      <SectionCard title="Allison — Historical Reconciliation" icon="Sparkles" data-testid="allison-reconciliation">
        <EmptyState icon="Sparkles" title="Nothing to reconcile" description={`Scanned ${data?.summary?.conversations_scanned ?? 0} historical conversation(s). No estimate or payment evidence needs reconciling.`} />
      </SectionCard>
    );
  }
  const open = data.summary?.needs_reconciliation || 0;

  return (
    <SectionCard title="Allison — Historical Reconciliation" icon="Sparkles" data-testid="allison-reconciliation"
      action={<Pill tone={open > 0 ? "warning" : "success"} data-testid="allison-summary">{open > 0 ? `${open} to reconcile` : "All reconciled"}</Pill>}>
      <p className="text-xs text-slate-500 mb-3 flex items-start gap-1.5"><Icons.ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />Advisory only. Allison never changes an estimate or payment — you review the detected evidence and confirm in a governed dialog.</p>
      <div className="space-y-3" data-testid="allison-proposals">
        {proposals.map((p) => {
          const sm = STATUS_META[p.status] || STATUS_META.advisory;
          const EvIcon = Icons[EV_ICON[p.evidence_type]] || Icons.Circle;
          const d = p.detected || {};
          const reconciled = p.status === "already_reconciled";
          return (
            <div key={p.id} className="rounded-xl border border-slate-200 p-3.5" data-testid={`allison-proposal-${p.evidence_type}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2.5 min-w-0">
                  <span className="h-8 w-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><EvIcon className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800">{d.label}
                      {d.amount != null && <span className="ml-2 text-slate-600 font-medium">{usd(d.amount, d.currency || "USD")}</span>}
                      {d.method && <span className="ml-1.5 text-xs text-slate-500">via {PAYMENT_METHOD_LABELS[d.method] || d.method}</span>}
                    </div>
                    {d.quote && <div className="text-xs text-slate-500 mt-0.5 italic line-clamp-2">“{d.quote}”</div>}
                    {d.evidence_reference?.conversation_title && (
                      <div className="text-[11px] text-slate-400 mt-1 inline-flex items-center gap-1">
                        <Icons.MessageSquare className="h-3 w-3" />{d.evidence_reference.conversation_title}
                        {d.evidence_reference.message_number ? ` · msg #${d.evidence_reference.message_number}` : ""}
                        {d.evidence_reference.date ? ` · ${d.evidence_reference.date}` : ""}
                      </div>
                    )}
                  </div>
                </div>
                <Pill tone={sm.tone}><span className="inline-flex items-center gap-1">{(() => { const I = Icons[sm.icon] || Icons.Info; return <I className="h-3 w-3" />; })()}{sm.label}</span></Pill>
              </div>
              <div className="flex items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-50 flex-wrap">
                <div className="text-xs text-slate-500">
                  <span className="text-slate-400">Suggested:</span> <span className="font-medium text-slate-700">{p.suggested_action.label}</span>
                  {p.canonical_state && <span className="ml-2 text-slate-400">· Canonical: <span className="font-mono">{p.canonical_state.estimate_number}</span> ({String(p.canonical_state.status).replace(/_/g, " ")})</span>}
                  {p.notes && <div className="text-slate-400 mt-0.5">{p.notes}</div>}
                </div>
                {reconciled
                  ? (p.canonical_state?.estimate_id
                      ? <ActionButton variant="ghost" icon="ArrowRight" onClick={() => navigate(`/sales/estimates/${p.canonical_state.estimate_id}`)} data-testid={`allison-view-${p.evidence_type}`}>View Estimate</ActionButton>
                      : <span className="text-xs text-emerald-600 font-medium">Reconciled</span>)
                  : (p.suggested_action.code === "review_evidence"
                      ? <ActionButton variant="secondary" icon="Eye" onClick={() => act(p)} data-testid={`allison-review-${p.evidence_type}`}>Review Evidence</ActionButton>
                      : <ActionButton icon="ClipboardCheck" onClick={() => act(p)} data-testid={`allison-confirm-${p.evidence_type}`}>Review & Confirm</ActionButton>)}
              </div>
            </div>
          );
        })}
      </div>

      <RecordAcceptanceDialog open={dialog?.kind === "acceptance"} onClose={() => setDialog(null)}
        estimateId={dialog?.estimateId} clientName={clientName} prefill={dialog?.prefill} onDone={done} />
      <RecordPaymentDialog open={dialog?.kind === "payment"} onClose={() => setDialog(null)}
        estimateId={dialog?.estimateId} currency={dialog?.currency} remaining={0} prefill={dialog?.prefill} onDone={done} />
    </SectionCard>
  );
}
